import { useRef, useState } from "react";
import { useLocation } from "wouter";
import { AuthShell, Field, SubmitButton } from "./AuthShell";
import { useCaptcha } from "./useCaptcha";
import { t } from "../../locale";
import { leaveRoomAs } from "../../lib/api";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../stores/auth";
import { useGame } from "../../stores/game";

export const SignIn = () => {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const guestTokenRef = useRef<string | null>(null);

  const captcha = useCaptcha(setError);
  const signIn = useAuth((s) => s.signIn);
  const inRoom = useGame((s) => s.phase !== null);
  const reconnectAsCurrentUser = useGame((s) => s.reconnectAsCurrentUser);

  const ready =
    email.trim().length > 0 &&
    password.length > 0 &&
    (captcha.token !== null || captcha.bypassed);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      /*
       * Credentials first. Leaving before they are proven means a mistyped
       * password throws the player out of a game they are in the middle of —
       * and `signIn` throws on failure, so nothing below would run to put them
       * back.
       *
       * Signing in does not disturb the socket: it authenticated with the token
       * it held at connect, and only `reconnectAsCurrentUser` rebuilds it. So
       * the connection is still the guest's here, which is precisely what gives
       * the Leave below the standing to succeed.
       */
      /*
       * Captured before the session is replaced, and kept across retries.
       * Signing in elsewhere does not revoke it, so it stays usable for the one
       * thing the guest still has to do: give up their seat. Re-reading the
       * session on a retry would hand back the *new* user's token, which has no
       * standing to release the guest's membership — so the first capture is
       * the only one that can ever work.
       */
      if (inRoom && !guestTokenRef.current) {
        guestTokenRef.current =
          (await supabase.auth.getSession()).data.session?.access_token ?? null;
      }

      await signIn(email.trim(), password, captcha.token ?? undefined);

      /*
       * One call, not two. The socket command and this route now run the same
       * server-side operation — mutation and broadcasts together — so issuing
       * both raced: whichever landed first left the other to find no room, and
       * if the socket lost, nobody in the room heard PlayerLeft or HostChanged.
       *
       * HTTP is the one to keep, because it is awaited and answered: the seat
       * is known to be released rather than assumed.
       *
       * Gated on the captured token, never on `inRoom`.
       * `reconnectAsCurrentUser` below clears the room state, so `inRoom` is
       * false by the time a retry runs — gating on it meant the retry skipped
       * the cleanup, reported success, and discarded the one token that could
       * have released the seat. A single transient failure orphaned the guest
       * permanently. The token outlives the room state precisely so it can
       * answer "is there still a seat to give up?" after the local view of the
       * room is gone.
       */
      let released = true;
      if (guestTokenRef.current) {
        released = await leaveRoomAs(guestTokenRef.current);
      }

      /*
       * The socket is rebuilt whether or not the seat was released. It still
       * holds the guest's token, so leaving it up means the browser is signed
       * in as one person and connected as another — and every command it sends
       * is attributed to the wrong user. A stale seat is untidy; two live
       * identities is incoherent.
       */
      await reconnectAsCurrentUser();

      if (!released) {
        setError(t.auth.leaveFailed);
        return;
      }
      guestTokenRef.current = null;

      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.auth.genericFailure);
      captcha.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t.auth.signIn} error={error} allowInRoom>
      <Field
        label={t.auth.email}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
      />
      <Field
        label={t.auth.password}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
      />
      {captcha.widget}
      <SubmitButton
        label={t.auth.signIn}
        disabled={!ready || busy}
        onClick={submit}
      />
      <div className="flex flex-col items-center gap-2">
        <button
          className="min-h-0 px-2 py-2 text-sm text-ink-dim underline"
          onClick={() => navigate("/auth/forgot")}
        >
          {t.auth.forgotPassword}
        </button>
        <button
          className="min-h-0 px-2 py-2 text-sm text-ink-dim underline"
          onClick={() => navigate("/auth/register")}
        >
          {t.auth.noAccount}
        </button>
      </div>
    </AuthShell>
  );
};
